import { AlertTriangle, CheckCircle2, Clock3, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AnalyticsMaintenanceJob, AnalyticsMaintenanceStatus, SessionStatus } from '../shared/portalTypes';

const RefreshIntervalMilliseconds = 60_000;

export function JobsView({ active, sessionStatus }: { active: boolean; sessionStatus: SessionStatus }): ReactNode {
  const [status, setStatus] = useState<AnalyticsMaintenanceStatus>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clock, setClock] = useState(() => Date.now());

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/portal/try-it/customer-assets-api/v1/analytics-maintenance', {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) {
        const body = await response.json().catch(() => undefined) as { detail?: string; error?: string } | undefined;
        throw new Error(body?.detail ?? body?.error ?? `Job status returned ${response.status}.`);
      }
      setStatus((await response.json()) as AnalyticsMaintenanceStatus);
      setClock(Date.now());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load job status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    void loadStatus();
    const refreshTimer = window.setInterval(() => void loadStatus(), RefreshIntervalMilliseconds);
    const clockTimer = window.setInterval(() => setClock(Date.now()), 30_000);
    return () => {
      window.clearInterval(refreshTimer);
      window.clearInterval(clockTimer);
    };
  }, [active, loadStatus]);

  const nextJob = useMemo(() => status?.jobs
    .filter((job) => job.enabled && job.nextRunUtc)
    .sort((left, right) => Date.parse(left.nextRunUtc!) - Date.parse(right.nextRunUtc!))[0], [status]);

  return (
    <section className="portal-view jobs-view" aria-label="Analytics jobs">
      <header className="jobs-heading">
        <div>
          <p className="eyebrow">Operations</p>
          <h2>Analytics jobs</h2>
          <p>Live schedule and execution history for the connected customer database.</p>
        </div>
        <button className="icon-button jobs-refresh" type="button" title="Refresh job status" aria-label="Refresh job status" disabled={loading} onClick={() => void loadStatus()}>
          {loading ? <Loader2 className="spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
        </button>
      </header>

      <div className="jobs-context" aria-label="Job status context">
        <span><strong>{sessionStatus.environment.toUpperCase()}</strong><small>Environment</small></span>
        <span><strong>{status?.customerCode ?? sessionStatus.customerCode ?? 'Unavailable'}</strong><small>Customer</small></span>
        <span>
          <strong>{nextJob?.nextRunUtc ? formatRelativeTime(nextJob.nextRunUtc, clock) : 'No run scheduled'}</strong>
          <small>{nextJob ? `Next: ${nextJob.label}` : 'Next run'}</small>
        </span>
        <span><strong>{status?.observedAtUtc ? formatTimestamp(status.observedAtUtc) : 'Waiting'}</strong><small>Last checked</small></span>
      </div>

      {error && (
        <div className="jobs-alert error" role="alert">
          <XCircle aria-hidden="true" />
          <div><strong>Job status unavailable</strong><span>{error}</span></div>
          <button type="button" onClick={() => void loadStatus()}>Retry</button>
        </div>
      )}

      {!status && loading && (
        <div className="jobs-loading" aria-live="polite">
          <Loader2 className="spin" aria-hidden="true" />
          <span>Loading job status</span>
        </div>
      )}

      {status && (
        <div className="jobs-table-wrap">
          <table className="jobs-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Status</th>
                <th>Schedule</th>
                <th>Last run</th>
                <th>Duration</th>
                <th>Next run</th>
              </tr>
            </thead>
            <tbody>
              {status.jobs.map((job) => (
                <JobRow key={job.operation} job={job} now={clock} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function JobRow({ job, now }: { job: AnalyticsMaintenanceJob; now: number }): ReactNode {
  return (
    <tr>
      <td>
        <span className="jobs-name"><JobIcon status={job.status} /><strong>{job.label}</strong></span>
        <small>{job.operation}</small>
      </td>
      <td><span className={`job-status ${job.status}`}>{formatStatus(job.status)}</span></td>
      <td><span>{job.schedule}</span></td>
      <td>
        <span>{job.lastCompletedAtUtc ? formatTimestamp(job.lastCompletedAtUtc) : 'Not run yet'}</span>
        {job.errorMessage && <small className="job-error" title={job.errorMessage}>{job.errorMessage}</small>}
        {!job.errorMessage && job.resultSummary && <small title={job.resultSummary}>{job.resultSummary}</small>}
      </td>
      <td><span>{formatDuration(job.lastDurationMilliseconds)}</span></td>
      <td>
        <span>{job.nextRunUtc ? formatTimestamp(job.nextRunUtc) : 'Disabled'}</span>
        {job.nextRunUtc && <small>{formatRelativeTime(job.nextRunUtc, now)}</small>}
      </td>
    </tr>
  );
}

function JobIcon({ status }: { status: AnalyticsMaintenanceJob['status'] }): ReactNode {
  if (status === 'failed') return <XCircle aria-hidden="true" />;
  if (status === 'running') return <Loader2 className="spin" aria-hidden="true" />;
  if (status === 'succeeded') return <CheckCircle2 aria-hidden="true" />;
  if (status === 'never-run') return <AlertTriangle aria-hidden="true" />;
  return <Clock3 aria-hidden="true" />;
}

function formatStatus(status: AnalyticsMaintenanceJob['status']): string {
  if (status === 'never-run') return 'Not run';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatTimestamp(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return 'Unavailable';
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
  }).format(timestamp);
}

function formatDuration(milliseconds: number | undefined): string {
  if (milliseconds === undefined || milliseconds < 0) return 'Unavailable';
  const totalSeconds = Math.round(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatRelativeTime(value: string, now: number): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return 'Unavailable';
  const differenceMinutes = Math.round((timestamp - now) / 60_000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (Math.abs(differenceMinutes) < 60) return formatter.format(differenceMinutes, 'minute');
  const differenceHours = Math.round(differenceMinutes / 60);
  if (Math.abs(differenceHours) < 48) return formatter.format(differenceHours, 'hour');
  return formatter.format(Math.round(differenceHours / 24), 'day');
}